/**
 * Server-side save and load (CLAUDE.md sections 13, 21.5).
 *
 * S1 kept sheets in localStorage, which meant a sheet lived in one browser and died
 * with a cleared cache. They now belong to a workspace and follow the engineer
 * between machines.
 *
 * localStorage does not go away; its job changes. It stops being the record and
 * becomes the **unsaved draft**, which is what section 14.1 asked of it in the first
 * place: "Engineers lose work; do not let a dropped connection cost them an hour."
 * The server holds what was saved, the browser holds what has been typed since.
 */
import { api } from '@/api/client'
import type { SheetDoc } from './model/types'

export interface SheetSummary {
  readonly id: number
  readonly title: string
  readonly slug: string | null
  readonly visibility: 'private' | 'workspace'
  readonly cell_count: number
  readonly engine_version: string
  readonly row_version: number
  readonly owner_id: number
  readonly created_at: string
  readonly updated_at: string | null
  readonly can_edit: boolean
  /** Publication state (21.3). `published_version` is what a bare link resolves to. */
  readonly published_count: number
  readonly published_version: number | null
  readonly has_unpublished_changes: boolean
}

export interface SheetDetail extends SheetSummary {
  readonly document: SheetDoc
}

export function listSheets(signal?: AbortSignal): Promise<SheetSummary[]> {
  return api.get<SheetSummary[]>('/sheets', signal)
}

export function getSheet(id: number, signal?: AbortSignal): Promise<SheetDetail> {
  return api.get<SheetDetail>(`/sheets/${id}`, signal)
}

export function createSheet(input: {
  title: string
  document: SheetDoc
  visibility?: 'private' | 'workspace'
}): Promise<SheetDetail> {
  return api.post<SheetDetail>('/sheets', input)
}

export function updateSheet(
  id: number,
  input: {
    row_version: number
    title?: string
    document?: SheetDoc
    visibility?: 'private' | 'workspace'
  },
): Promise<SheetDetail> {
  return api.patch<SheetDetail>(`/sheets/${id}`, input)
}

export function deleteSheet(id: number): Promise<void> {
  return api.delete<void>(`/sheets/${id}`)
}

/* --- the local draft layer ------------------------------------------------------ */

const DRAFT_PREFIX = 'calcdesk.sheet.draft.'

export interface Draft {
  readonly document: SheetDoc
  /** The server version this draft was edited from, so a stale save is detectable. */
  readonly baseRowVersion: number
  readonly savedAt: string
}

export function readDraft(id: number): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_PREFIX + id)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Draft
    return Array.isArray(parsed.document?.cells) ? parsed : null
  } catch {
    return null
  }
}

export function writeDraft(id: number, document: SheetDoc, baseRowVersion: number): void {
  try {
    localStorage.setItem(
      DRAFT_PREFIX + id,
      JSON.stringify({ document, baseRowVersion, savedAt: new Date().toISOString() }),
    )
  } catch {
    // Quota, private mode, storage disabled. Losing the draft is bad; throwing in
    // the middle of a keystroke is worse.
  }
}

export function clearDraft(id: number): void {
  try {
    localStorage.removeItem(DRAFT_PREFIX + id)
  } catch {
    // Nothing useful to do.
  }
}

/* --- publishing (CLAUDE.md 21.3) ------------------------------------------------ */

export interface SheetVersionSummary {
  readonly id: number
  readonly version: number
  readonly cell_count: number
  readonly engine_version: string
  readonly note: string | null
  readonly published_at: string
  readonly published_by: number
  readonly superseded_by: number | null
}

export interface SheetVersionDetail extends SheetVersionSummary {
  readonly document: SheetDoc
}

/**
 * Freezes the sheet as it stands. The version created is never rewritten, so a link
 * handed to a colleague keeps resolving to the numbers they were sent.
 */
export function publishSheet(
  id: number,
  input: { note?: string; slug?: string } = {},
): Promise<SheetVersionDetail> {
  return api.post<SheetVersionDetail>(`/sheets/${id}/publish`, input)
}

export function listVersions(id: number, signal?: AbortSignal): Promise<SheetVersionSummary[]> {
  return api.get<SheetVersionSummary[]>(`/sheets/${id}/versions`, signal)
}

export function getVersion(
  id: number,
  version: number,
  signal?: AbortSignal,
): Promise<SheetVersionDetail> {
  return api.get<SheetVersionDetail>(`/sheets/${id}/versions/${version}`, signal)
}

/* --- assets (docs/SHEETS.md 3, 4.4) --------------------------------------------- */

export interface SheetAsset {
  readonly id: number
  readonly sheet_id: number
  readonly uploader_id: number | null
  readonly filename: string
  /** Of what the server *wrote* after re-encoding -- a GIF comes back as image/png. */
  readonly media_type: string
  readonly byte_size: number
  readonly width: number
  readonly height: number
  readonly created_at: string
}

/** What the picker accepts. The server sniffs the bytes and decides for itself. */
export const SHEET_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const

/**
 * Upload one image into a sheet. Only a sheet that exists on the server can hold
 * one, which is why a brand-new unsaved sheet shows a hint instead of a picker.
 */
export function uploadSheetAsset(sheetId: number, file: File): Promise<SheetAsset> {
  const form = new FormData()
  form.append('file', file, file.name)
  return api.upload<SheetAsset>(`/sheets/${sheetId}/assets`, form)
}

export function listSheetAssets(sheetId: number, signal?: AbortSignal): Promise<SheetAsset[]> {
  return api.get<SheetAsset[]>(`/sheets/${sheetId}/assets`, signal)
}

export function deleteSheetAsset(sheetId: number, assetId: number): Promise<void> {
  return api.delete<void>(`/sheets/${sheetId}/assets/${assetId}`)
}

/**
 * The path the image is fetched from, with the bearer token, into a `blob:` URL
 * (`useBlobUrl`). Never used as an `<img src>` directly: the endpoint needs the
 * token, and the response carries the sandbox header that a static path would not.
 */
export function sheetAssetFilePath(sheetId: number, assetId: number): string {
  return `/api/v1/sheets/${sheetId}/assets/${assetId}/file`
}
